import React, { useState, useEffect, useRef } from "react";
import { Breadcrumb } from "@gull";
import BootstrapTable from "react-bootstrap-table-next";
import { Modal, Button } from "react-bootstrap";
import queryString from "app/services/queryStringService";
import _ from "lodash";
import { Helmet } from "react-helmet";
import { AiFillEye, AiFillEdit } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import swal from "sweetalert2";
import { NotificationManager } from "react-notifications";
import { Formik } from "formik";
import * as yup from "yup";
import Tooltip from "rc-tooltip";
import { RenderCheckboxInput } from "app/common/renderInput";
import paginationFactory from "react-bootstrap-table2-paginator";
import {
  getApplication,
  addApplication,
  editApplication,
  deleteApplication,
} from "app/services/applicationService";
import usePagePermission from "app/hooks/usePagePermission";

const ApplicationSettings = () => {
  const { canAdd, canEdit, canDelete } = usePagePermission();
  // console.log({ canAdd, canEdit, canDelete });

  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [sizePerPage, setSizePerPage] = useState(5);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memId, setMemId] = useState(0);
  const [showBannerPreview, setShowBannerPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [bannerInformation, setBannerInformation] = useState({
    id: 0,
    module: "",
    settingName: "",
    description: "",
    settingValue: "",
    isActive: false,
  });

  const onEditApplication = (row) => {
    setBannerInformation({
      id: row.id,
      module: row.module,
      settingName: row.settingName,
      description: row.description,
      settingValue: row.settingValue,
      isActive: row.isActive,
    });

    setShowPopup(true);
  };

  const actionFormatter = (cell, row) => {
    return (
      <div
        className="d-flex justify-content-center"
        style={{ cursor: "pointer" }}
      >
        <span>
          <Tooltip placement="top" overlay="Delete">
            <MdDelete
              onClick={() => onDeleteApplication(row)}
              size="1.5em"
              color="red"
              variant="danger"
              className="mx-4"
            />
          </Tooltip>
        </span>
        <span>
          <Tooltip placement="top" overlay="Modify">
            <AiFillEdit
              onClick={() => onEditApplication(row)}
              size="1.5em"
              color="black"
            />
          </Tooltip>
        </span>
      </div>
    );
  };

  const onDeleteApplication = async (row) => {
    swal
      .fire({
        title: `Are you sure you want to Delete this Application?`,
        icon: "warning",
        type: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      })
      .then(async (result) => {
        if (result.value) {
          const result = await deleteApplication(row.id);
          NotificationManager.error(
            "Application deleted successfully",
            "success",
            5000
          );
          loadBannerService();
        } else {
          swal.fire({
            title: "Cancelled!",
            text: "Permission denied.",
            type: "error",
            icon: "error",
            timer: 1500,
          });
        }
      });
  };

  const closeModal = () => {
    setShowPopup(false);
    setBannerInformation({
      id: 0,
      module: "",
      settingName: "",
      description: "",
      settingValue: "",
      isActive: false,
    });
  };

  const saveBannerInformation = async (values) => {
    let formData = new FormData();
    formData.append("id", values.id);

    formData.append("module", values.module);
    formData.append("settingName", values.settingName);
    formData.append("settingValue", values.settingValue);

    formData.append("description", values.description);
    formData.append("isActive", values.isActive);

    const item = {
      id: values.id,
      module: values.module,
      settingName: values.settingName,
      settingValue: values.settingValue,
      description: values.description,
      isActive: values.isActive,
    };

    if (values.id > 0) {
      const result = await editApplication(values.id, item);
      NotificationManager.success(
        "Application updated successfully ",
        "Success",
        5000
      );
    } else {
      const addResult = await addApplication(item);
      NotificationManager.success(
        " Application added successfully ",
        "Success",
        5000
      );
      console.log(addResult);
    }
    loadBannerService();
    closeModal();
  };

  const validationSchema = yup.object().shape({
    module: yup.string().trim().required("module is required."),
    settingName: yup.string().required("settings name is required."),
    settingValue: yup.string().required("settings value is required."),
    description: yup.string().required("settings description is required."),
    isActive: yup.boolean().required("settings status is required."),

    id: yup.number().required(),
  });

  const columns = [
    {
      dataField: "module",
      text: "Module",
      headerStyle: (colum, colIndex) => {
        return { textAlign: "center" };
      },
    },
    {
      dataField: "settingName",
      text: "Settings Name",
      headerStyle: (colum, colIndex) => {
        return { textAlign: "center" };
      },
    },
    {
      dataField: "settingValue",
      text: "settings Value",
      headerStyle: (colum, colIndex) => {
        return { textAlign: "center" };
      },
    },

    {
      dataField: "id",
      text: "Action",
      headerStyle: (colum, colIndex) => {
        return { textAlign: "center" };
      },
      formatter: actionFormatter,
    },
  ];

  const rowStyle = (row, rowIndex) => {
    const style = {};
    style.textAlign = "center";
    style.fontWeight = "600";
    style.verticalAlign = "center";
    return style;
  };

  const loadBannerService = async () => {
    const query = queryString.getQueryStringFromObj({
      pageNumber: page,
      pageSize: sizePerPage,
    });
    if (dataCount !== 0) {
      setLoading(true);
    }

    const response = await getApplication(query);
    const data = response.data.data;
    const paggingData = JSON.parse(response.headers["x-pagination"]);
    setLoading(false);
    setData(data);
    setDataCount(paggingData.TotalCount);
    setPage(paggingData.CurrentPage);
    setSizePerPage(paggingData.PageSize);
  };

  useEffect(() => {
    loadBannerService();
  }, []);

  const handleTableChange = async (type, { page, sizePerPage }) => {
    const currentIndex = (page - 1) * sizePerPage;
    setPage(page);
    setSizePerPage(sizePerPage);
    setTimeout(async () => {
      await loadBannerService();
    }, 10);
  };

  const sizePerPageRenderer = ({
    options,
    currSizePerPage,
    onSizePerPageChange,
  }) => (
    <>
      <label className="mr-2">Page Size</label>
      <div className="btn-group mr-4" role="group">
        {options.map((option) => {
          const isSelect = currSizePerPage === `${option.page}`;
          return (
            <button
              key={option.text}
              type="button"
              onClick={() => onSizePerPageChange(option.page)}
              className={`btn btn-sm ${
                isSelect ? "btn-primary" : "btn-secondary"
              }`}
            >
              {option.text}
            </button>
          );
        })}
      </div>
    </>
  );

  const paginationOptions = {
    paginationSize: 5,
    pageStartIndex: 1,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    showTotal: true,
    totalSize: dataCount,
    sizePerPageList: [
      {
        text: "5",
        value: 5,
      },
      {
        text: "15",
        value: 15,
      },
      {
        text: "20",
        value: 20,
      },
      {
        text: "50",
        value: 50,
      },
    ],
    sizePerPageRenderer,
  };

  return (
    <>
      {canAdd && (
        <Button
          onClick={() => setShowPopup(true)}
          className="btn btn-primary btn-md float-end"
        >
          Add Settings
        </Button>
      )}
      {/*----------------- Add & update modal start from here -------------*/}
      <Modal show={showPopup} onHide={closeModal}>
        <Formik
          initialValues={
            bannerInformation.id
              ? bannerInformation
              : {
                  id: 0,
                  module: "",
                  settingName: "",
                  settingValue: "",
                  description: "",
                  isActive: true,
                }
          }
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={saveBannerInformation}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} className="position-relative">
              <Modal.Header>
                <Modal.Title>
                  {bannerInformation.id ? "Edit" : "Add"} Application Settings
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="form-group">
                  <label htmlFor="module">Module</label>
                  <input
                    type="text"
                    name="module"
                    id="module"
                    value={values.module}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    placeholder="Module"
                  />
                  {touched.module && errors.module && (
                    <small className="form-text text-danger">
                      {errors.module}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="settingName">Settings name</label>
                  <input
                    type="text"
                    name="settingName"
                    id="settingName"
                    value={values.settingName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    placeholder="Settings Name"
                  />
                  {touched.settingName && errors.settingName && (
                    <small className="form-text text-danger">
                      {errors.settingName}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="settingValue">Settings value</label>
                  <br />
                  <input
                    type="text"
                    name="settingValue"
                    id="settingsValue"
                    value={values.settingValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    placeholder="Settings Value"
                  />
                  {touched.settingValue && errors.settingValue && (
                    <small className="form-text text-danger">
                      {errors.settingValue}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="description">Settings Description</label>
                  <br />
                  <input
                    type="text"
                    name="description"
                    id="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    placeholder="Settings description"
                  />
                  {touched.description && errors.description && (
                    <small className="form-text text-danger">
                      {errors.description}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <RenderCheckboxInput
                    type="switch"
                    name="isActive"
                    display="Active"
                    value={values.isActive}
                    error={errors.isActive}
                    onBlur={handleBlur}
                    touched={touched.isActive}
                    required
                    onChange={(e) => {
                      setFieldValue("isActive", e.currentTarget.checked);
                    }}
                  />
                  {touched.isActive && errors.isActive && (
                    <small className="form-text text-danger">
                      {errors.isActive}
                    </small>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button disabled={isSubmitting} variant="primary" type="submit">
                  {isSubmitting
                    ? "Please wait ..."
                    : values.id > 0
                    ? "Update"
                    : "+ Add"}
                </Button>
                <Button variant="danger" onClick={closeModal}>
                  Close
                </Button>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>

      {/*```````````````````` Preview Modal start from here ```````````````*/}
      <Modal
        show={showBannerPreview}
        onHide={() => setShowBannerPreview(false)}
      >
        <Modal.Header>
          <Modal.Title>Preview Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img src={previewImage} alt="Preview" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setShowBannerPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Helmet>
        <title>Application Setting</title>
      </Helmet>
      <Breadcrumb routeSegments={[{ name: "Application Settings" }]} />

      <div>
        <BootstrapTable
          hover
          remote
          keyField="id"
          data={data}
          columns={
            canEdit ? columns : columns.filter((x) => x.text !== "Action")
          }
          pagination={paginationFactory(paginationOptions)}
          onTableChange={handleTableChange}
          headerClasses="bg-primary text-white"
          bodyClasses="react-bootstrap-table-next-min-height"
          rowStyle={rowStyle}
        />
      </div>
    </>
  );
};

export default ApplicationSettings;
